import { useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import { auth } from "../../firebase";
import { TAG } from "../../components/applicationShared";
import { MELT_BUTTON } from "../../components/ui/melt";
import { cn } from "../../lib/utils";
import { isPremium, planLine, useEnsurePlan } from "../../utils/plan";
import { ErrorNote, SETTINGS_CARD, SettingsHeader, useSettings } from "./SettingsLayout";

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";
const PRICE_ID = "price_1SQj99FtjgTHULpAQNZz8ZJD";

const OUTLINE_BUTTON =
  "inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-melt-hair bg-white px-5 text-[15px] font-medium text-melt-text1 transition-colors hover:bg-melt-paper";

export default function PlanSettings() {
  const { userRecord, userRecordLoading } = useSettings();
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  useEnsurePlan(userRecord, userRecordLoading);
  const premium = isPremium(userRecord);
  const source = userRecord?.planSource;

  const upgrade = async () => {
    setError("");
    const user = auth.currentUser;
    if (!user) return setError("Please log in again to continue.");
    setRedirecting(true);
    try {
      const response = await axios.post(
        `${API_BASE}/create-checkout-session`,
        { priceId: PRICE_ID },
        { headers: { Authorization: `Bearer ${await user.getIdToken()}` } }
      );
      window.location.href = response.data.checkoutUrl;
    } catch {
      setError("Couldn't start checkout. Please try again.");
      setRedirecting(false);
    }
  };

  const manage = () => window.open("mailto:support@oncaramel.com?subject=Subscription Management", "_blank");

  const badge = premium ? (
    <span className={cn(TAG, "inline-flex items-center gap-1 bg-[#EAF4EF] text-[#2F6B54]")}>
      <Check size={12} strokeWidth={2} />
      {source === "comp" ? "Premium (comped)" : "Premium"}
    </span>
  ) : (
    <span className={cn(TAG, "bg-[#F2EDE4] text-melt-text1")}>Free</span>
  );

  return (
    <>
      <SettingsHeader title="Plan" description="Your Caramel plan and billing." />

      {userRecordLoading || (userRecord && !userRecord.plan) ? (
        <p className="font-plex text-xs text-melt-text3">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className={cn(SETTINGS_CARD, "flex flex-wrap items-center justify-between gap-4 px-6 py-5")}>
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2.5">
                  <p className="text-[15px] font-semibold text-melt-text1">Current plan</p>
                  {badge}
                </div>
                <p className="mt-1 text-sm text-melt-text2">{planLine(userRecord)}</p>
              </div>
            </div>
            {/* Beta and comped users have nothing to manage or buy */}
            {premium ? (
              source === "stripe" && (
                <button type="button" onClick={manage} className={OUTLINE_BUTTON}>
                  Manage subscription
                </button>
              )
            ) : (
              <button type="button" onClick={upgrade} disabled={redirecting} className={MELT_BUTTON}>
                {redirecting ? "Opening checkout…" : "Upgrade for $5/month"}
              </button>
            )}
          </div>

          {error && <ErrorNote>{error}</ErrorNote>}
        </div>
      )}
    </>
  );
}
